import { useThemeColors } from '@/shared/theme/useThemeColors';
import { typography, shadow, radius } from '@/shared/theme/tokens';

/**
 * Project-standard search field: leading search icon, themed input, optional
 * clear (×) button. Controlled.
 *
 * Replaces the ~9 hand-rolled icon-prefixed search inputs across the app so they
 * all look and behave identically.
 *
 * Props:
 *   value        — controlled string
 *   onChange     — (nextValue: string) => void   (receives the value, NOT the event)
 *   placeholder  — input placeholder
 *   onClear      — optional; when provided AND value non-empty, shows a × button
 *                  that calls it (defaults to onChange('') when omitted)
 *   ariaLabel    — accessible name (default "search")
 *   size         — 'sm' | 'md' (default 'md')
 *   containerStyle / inputStyle — escape hatches merged over the defaults
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  onClear,
  ariaLabel = 'search',
  size = 'md',
  autoFocus = false,
  onKeyDown,
  containerStyle,
  inputStyle,
}) {
  const c = useThemeColors();
  const pad = size === 'sm' ? '7px 30px 7px 32px' : '9px 36px 9px 38px';
  const iconSize = size === 'sm' ? 15 : 18;
  const fontSize = size === 'sm' ? 12 : 13;
  const handleClear = onClear ?? (() => onChange?.(''));

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, ...containerStyle }}>
      <span
        className="material-symbols-outlined"
        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: iconSize, color: c.textSubtle, pointerEvents: 'none' }}
      >
        search
      </span>
      <input
        aria-label={ariaLabel}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: c.bg,
          border: `1.5px solid ${c.borderSubtle}`,
          borderRadius: radius.lg,
          padding: pad,
          fontSize,
          fontFamily: typography.fontFamily.body,
          color: c.textPrimary,
          outline: 'none',
          boxShadow: shadow.search,
          ...inputStyle,
        }}
      />
      {value && (
        <button
          onClick={handleClear}
          aria-label="clear search"
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle }}>close</span>
        </button>
      )}
    </div>
  );
}
